import { useCallback, useEffect, useState } from 'react'
import type { SessionUser } from '@raporto/shared'
import { formatChf } from '@raporto/shared'
import { OfficeHead } from '../ui/OfficeShell.js'
import { notify, notifyError } from '../ui/Toasts.js'
import {
  createCatalogItem,
  updateCatalogItem,
  removeCatalogItem,
  type CatalogFields
} from '../net/records.js'
import { api, type BueroLeistung, type KatalogFilter } from '../net/apiClient.js'

/**
 * Der Leistungskatalog im Büro.
 *
 * Ein Katalog wächst und wird nie aufgeräumt, weil niemand weiss, was wegkann.
 * Die Spalte «verwendet» beantwortet genau das: eine Position mit null
 * Verwendungen ist ein Kandidat zum Deaktivieren, eine mit vierzig ist der
 * Kern des Betriebs.
 *
 * Gezählt werden nur abgeschlossene Belege. Ein Entwurf ist noch keine
 * Verwendung, sondern eine Absicht – und Absichten sind ein schlechter Grund,
 * eine Position zu behalten.
 *
 * Deaktivieren statt löschen: eine importierte Leistung steht auf alten
 * Rechnungen, und was auf einer Rechnung steht, verschwindet nicht mehr. Der
 * Server entscheidet das; hier steht nur, was er geantwortet hat.
 */

const datum = (iso: string | null): string =>
  iso ? new Date(`${iso.slice(0, 10)}T00:00:00`).toLocaleDateString('de-CH') : '–'

/** "85.50" oder "85,50" → 8550. Der Betrieb tippt beides. */
function zuRappen(text: string): number {
  const n = Number(String(text).replace(',', '.').trim())
  return Number.isFinite(n) ? Math.round(n * 100) : 0
}

const FILTER: Array<{ id: KatalogFilter; label: string }> = [
  { id: 'alle', label: 'Alle' },
  { id: 'arbeit', label: 'Arbeit' },
  { id: 'material', label: 'Material' },
  { id: 'unbenutzt', label: 'Nie verwendet' },
  { id: 'inaktiv', label: 'Deaktiviert' }
]

const LEER: CatalogFields = {
  code: '',
  name: '',
  unit: 'm²',
  unitPriceRp: 0,
  vatRate: 8.1,
  kind: 'arbeit'
}

export function OfficeCatalog({ session }: { session: SessionUser }): JSX.Element {
  const [filter, setFilter] = useState<KatalogFilter>('alle')
  const [suche, setSuche] = useState('')
  const [items, setItems] = useState<BueroLeistung[]>([])
  const [zaehler, setZaehler] = useState<Record<string, number>>({})
  const [laedt, setLaedt] = useState(true)
  const [bearbeitet, setBearbeitet] = useState<BueroLeistung | null>(null)
  const [neu, setNeu] = useState(false)
  const [busy, setBusy] = useState<string | null>(null)

  const actor = { tenantId: session.tenantId, userId: session.userId }

  const laden = useCallback(async () => {
    setLaedt(true)
    try {
      const r = await api.officeCatalog(filter, suche)
      setItems(r.items)
      setZaehler(r.zaehler as unknown as Record<string, number>)
    } catch (err) {
      notifyError(err)
    } finally {
      setLaedt(false)
    }
  }, [filter, suche])

  useEffect(() => {
    void laden()
  }, [laden])

  async function deaktivieren(l: BueroLeistung): Promise<void> {
    setBusy(l.id)
    try {
      const r = await removeCatalogItem(actor, l.id)
      // Der Server sagt, was passiert ist – die Oberfläche gibt es weiter,
      // statt eigene Vermutungen anzustellen.
      if (r.disposal === 'geloescht') notify.ok(r.message)
      else notify.info(r.message)
      await laden()
    } catch (err) {
      notifyError(err)
    } finally {
      setBusy(null)
    }
  }

  async function wiederAktivieren(l: BueroLeistung): Promise<void> {
    setBusy(l.id)
    try {
      await updateCatalogItem(
        actor,
        l.id,
        {
          code: l.code ?? '',
          name: l.name,
          unit: l.unit,
          unitPriceRp: l.unitPriceRp,
          vatRate: l.vatRate,
          kind: l.kind,
          active: true
        },
        l.version
      )
      notify.ok(`„${l.name}“ steht wieder in den Auswahllisten.`)
      await laden()
    } catch (err) {
      notifyError(err)
    } finally {
      setBusy(null)
    }
  }

  return (
    <>
      <OfficeHead
        titel="Leistungskatalog"
        unter={`${zaehler.alle ?? 0} Positionen · ${zaehler.unbenutzt ?? 0} davon nie verwendet`}
      >
        <button
          className="o-btn o-btn-primary"
          id="btn-neue-leistung"
          onClick={() => {
            setBearbeitet(null)
            setNeu(true)
          }}
        >
          Neue Position
        </button>
      </OfficeHead>

      {(neu || bearbeitet) && (
        <LeistungForm
          vorhanden={bearbeitet}
          onSpeichern={async (werte) => {
            if (bearbeitet) {
              await updateCatalogItem(actor, bearbeitet.id, werte, bearbeitet.version)
              notify.ok('Position geändert.')
            } else {
              await createCatalogItem(actor, werte)
              notify.ok('Position angelegt.')
            }
            setNeu(false)
            setBearbeitet(null)
            await laden()
          }}
          onAbbruch={() => {
            setNeu(false)
            setBearbeitet(null)
          }}
        />
      )}

      <div className="o-card">
        <div className="o-pad">
          <div className="o-suchzeile">
            <div className="o-filter" id="katalogfilter">
              {FILTER.map((f) => (
                <button
                  key={f.id}
                  id={`lfilter-${f.id}`}
                  className={f.id === filter ? 'o-chip aktiv' : 'o-chip'}
                  aria-pressed={f.id === filter}
                  onClick={() => setFilter(f.id)}
                >
                  {f.label}
                  <span className="o-chip-zahl">{zaehler[f.id] ?? 0}</span>
                </button>
              ))}
            </div>
            <div className="o-field o-feldsuche">
              <label htmlFor="leistung-suche">Suche</label>
              <input
                id="leistung-suche"
                value={suche}
                onChange={(e) => setSuche(e.target.value)}
                placeholder="Nummer, Bezeichnung oder Einheit"
                autoComplete="off"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="o-card" id="katalogliste">
        <div className="o-card-head">
          <h3>
            {FILTER.find((f) => f.id === filter)?.label}{' '}
            <span className="o-badge o-badge-accent">{items.length}</span>
          </h3>
          <span className="o-soft">
            Verwendungen aus abgeschlossenen Belegen gezählt – Entwürfe zählen nicht.
          </span>
        </div>

        {laedt ? (
          <div className="o-leer">Wird geladen …</div>
        ) : items.length === 0 ? (
          <div className="o-leer" id="katalog-leer">
            {suche.trim() ? (
              <>
                <strong>Kein Treffer.</strong>
                Gesucht wird über Nummer, Bezeichnung und Einheit.
              </>
            ) : (
              <>
                <strong>Nichts in dieser Auswahl.</strong>
              </>
            )}
          </div>
        ) : (
          <div className="o-tabelle">
            <table className="o-data">
              <thead>
                <tr>
                  <th style={{ width: 110 }}>Nummer</th>
                  <th>Bezeichnung</th>
                  <th style={{ width: 100 }}>Einheit</th>
                  <th style={{ width: 110 }}>Art</th>
                  <th className="o-num" style={{ width: 115 }}>
                    Preis
                  </th>
                  <th className="o-num" style={{ width: 80 }}>
                    MWST
                  </th>
                  <th className="o-num" style={{ width: 110 }}>
                    Verwendet
                  </th>
                  <th style={{ width: 130 }}>Zuletzt</th>
                  <th className="o-num" style={{ width: 120 }}>
                    Umsatz
                  </th>
                  <th style={{ width: 210 }} />
                </tr>
              </thead>
              <tbody>
                {items.map((l) => (
                  <tr
                    key={l.id}
                    id={`leistung-${l.id}`}
                    className={l.active ? undefined : 'inaktiv'}
                  >
                    <td className="o-mono">{l.code || '–'}</td>
                    <td>
                      {l.name}
                      {!l.active && (
                        <span className="o-badge o-badge-neutral" style={{ marginLeft: 8 }}>
                          deaktiviert
                        </span>
                      )}
                    </td>
                    <td className="o-soft">{l.unit}</td>
                    <td className="o-soft">{l.kind === 'material' ? 'Material' : 'Arbeit'}</td>
                    <td className="o-num">{formatChf(l.unitPriceRp)}</td>
                    <td className="o-num">{l.vatRate} %</td>
                    <td className="o-num">
                      {l.verwendungen > 0 ? (
                        l.verwendungen
                      ) : (
                        <span className="o-badge o-badge-neutral">nie</span>
                      )}
                    </td>
                    <td className="o-soft">{datum(l.letzteVerwendung)}</td>
                    <td className="o-num">{l.umsatzRp > 0 ? formatChf(l.umsatzRp) : '–'}</td>
                    <td>
                      <div className="office-actions">
                        <button
                          className="o-btn o-btn-ghost"
                          id={`lbearbeiten-${l.id}`}
                          disabled={busy !== null}
                          onClick={() => {
                            setNeu(false)
                            setBearbeitet(l)
                          }}
                        >
                          Ändern
                        </button>
                        {l.active ? (
                          <button
                            className="o-btn o-btn-ghost"
                            id={`ldeaktivieren-${l.id}`}
                            disabled={busy !== null}
                            onClick={() => void deaktivieren(l)}
                          >
                            Deaktivieren
                          </button>
                        ) : (
                          <button
                            className="o-btn o-btn-ghost"
                            id={`laktivieren-${l.id}`}
                            disabled={busy !== null}
                            onClick={() => void wiederAktivieren(l)}
                          >
                            Wieder aktivieren
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <p className="o-soft" style={{ marginTop: -8 }}>
        Deaktivieren nimmt eine Position aus den Auswahllisten. Auf bestehenden Belegen bleibt sie
        stehen – Belegzeilen tragen Text und Preis selbst und ändern sich nie rückwirkend.
      </p>
    </>
  )
}

// ---------------------------------------------------------------------------

function LeistungForm({
  vorhanden,
  onSpeichern,
  onAbbruch
}: {
  vorhanden: BueroLeistung | null
  onSpeichern: (werte: CatalogFields) => Promise<void>
  onAbbruch: () => void
}): JSX.Element {
  const [code, setCode] = useState(vorhanden?.code ?? LEER.code)
  const [name, setName] = useState(vorhanden?.name ?? LEER.name)
  const [unit, setUnit] = useState(vorhanden?.unit ?? LEER.unit)
  const [preis, setPreis] = useState(((vorhanden?.unitPriceRp ?? 0) / 100).toFixed(2))
  const [kind, setKind] = useState<'arbeit' | 'material'>(vorhanden?.kind ?? LEER.kind)
  const [busy, setBusy] = useState(false)

  return (
    <div className="o-card" id="leistung-form">
      <div className="o-card-head">
        <h3>{vorhanden ? `Position ändern: ${vorhanden.name}` : 'Neue Position'}</h3>
      </div>
      <div className="o-pad">
        <div className="o-grid o-grid-3">
          <div className="o-field">
            <label htmlFor="l-code">Nummer</label>
            <input
              autoComplete="off"
              id="l-code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
            />
          </div>
          <div className="o-field" style={{ gridColumn: 'span 2' }}>
            <label htmlFor="l-name">Bezeichnung</label>
            <input
              autoComplete="off"
              id="l-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div className="o-field">
            <label htmlFor="l-unit">Einheit</label>
            <input
              autoComplete="off"
              id="l-unit"
              value={unit}
              onChange={(e) => setUnit(e.target.value)}
            />
          </div>
          <div className="o-field">
            <label htmlFor="l-preis">Preis CHF</label>
            <input
              autoComplete="off"
              id="l-preis"
              inputMode="decimal"
              value={preis}
              onChange={(e) => setPreis(e.target.value)}
            />
          </div>
          <div className="o-field">
            <label htmlFor="l-kind">Art</label>
            <select
              id="l-kind"
              value={kind}
              onChange={(e) => setKind(e.target.value as 'arbeit' | 'material')}
            >
              <option value="arbeit">Arbeit</option>
              <option value="material">Material</option>
            </select>
          </div>
        </div>

        <div className="office-actions" style={{ marginTop: 18 }}>
          <button
            className="o-btn o-btn-primary"
            id="btn-leistung-speichern"
            disabled={busy || name.trim().length === 0}
            onClick={async () => {
              setBusy(true)
              try {
                await onSpeichern({
                  code: code.trim(),
                  name: name.trim(),
                  unit: unit.trim() || 'Psch.',
                  unitPriceRp: zuRappen(preis),
                  /*
                    Der Steuersatz steht nicht mehr an der einzelnen Leistung.

                    Er gehört dem Betrieb, nicht der Position: ein
                    Handwerksbetrieb verrechnet zum Normalsatz, und wer nicht
                    steuerpflichtig ist, verrechnet ohne. Beides steht in den
                    Einstellungen. Solange es hier ein Feld gab, konnte ein
                    Tippfehler eine Rechnung mit zwei Steuerzeilen erzeugen –
                    und das fällt erst beim Treuhänder auf. Der Server setzt
                    den Satz; was hier steht, ist nur die Weitergabe des
                    bekannten Werts.
                  */
                  vatRate: vorhanden?.vatRate ?? 8.1,
                  kind
                })
              } catch (err) {
                notifyError(err)
              } finally {
                setBusy(false)
              }
            }}
          >
            {busy ? 'Speichert …' : 'Speichern'}
          </button>
          <button className="o-btn o-btn-ghost" id="btn-leistung-abbrechen" onClick={onAbbruch}>
            Abbrechen
          </button>
        </div>
      </div>
    </div>
  )
}
